import React, { useEffect, useState } from 'react';
import { Search, Plus, Eye, Trash2, Check, X } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { getAllBookings, updateBooking, deleteBooking, createManualBooking } from '../../utils/adminApi';
import { getServices } from '../../utils/api';
import { mockAddOns } from '../../utils/mock';

const AdminBookings = () => {
  const [bookings, setBookings] = useState([]);
  const [filteredBookings, setFilteredBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [services, setServices] = useState([]);

  // Manual booking form
  const [manualBooking, setManualBooking] = useState({
    serviceId: '',
    addOns: [],
    date: '',
    time: '',
    customerName: '',
    email: '',
    phone: '',
    address: '',
    carModel: '',
    notes: '',
    paymentMethod: 'cash'
  });

  useEffect(() => {
    fetchBookings();
    fetchServices();
  }, []);

  useEffect(() => {
    filterBookings();
  }, [bookings, search, statusFilter]);

  const fetchServices = async () => {
    try {
      const data = await getServices();
      setServices(data);
    } catch (error) {
      console.error('Error fetching services:', error);
    }
  };

  const fetchBookings = async () => {
    try {
      setLoading(true);
      const data = await getAllBookings();
      setBookings(data);
    } catch (error) {
      console.error('Error fetching bookings:', error);
      toast.error('Failed to load bookings');
    } finally {
      setLoading(false);
    }
  };

  const filterBookings = () => {
    let filtered = bookings;

    if (statusFilter !== 'all') {
      filtered = filtered.filter(b => b.status === statusFilter);
    }

    if (search) {
      filtered = filtered.filter(b =>
        b.customerName.toLowerCase().includes(search.toLowerCase()) ||
        b.email.toLowerCase().includes(search.toLowerCase()) ||
        b.bookingId.toLowerCase().includes(search.toLowerCase())
      );
    }

    setFilteredBookings(filtered);
  };

  const handleStatusUpdate = async (bookingId, newStatus) => {
    try {
      await updateBooking(bookingId, { status: newStatus });
      toast.success('Booking status updated');
      fetchBookings();
    } catch (error) {
      toast.error('Failed to update booking');
    }
  };

  const handleDelete = async (bookingId) => {
    if (window.confirm('Are you sure you want to delete this booking?')) {
      try {
        await deleteBooking(bookingId);
        toast.success('Booking deleted');
        fetchBookings();
      } catch (error) {
        toast.error('Failed to delete booking');
      }
    }
  };

  const handleCreateManual = async () => {
    try {
      await createManualBooking(manualBooking);
      toast.success('Manual booking created');
      setShowCreateDialog(false);
      setManualBooking({
        serviceId: '',
        addOns: [],
        date: '',
        time: '',
        customerName: '',
        email: '',
        phone: '',
        address: '',
        carModel: '',
        notes: '',
        paymentMethod: 'cash'
      });
      fetchBookings();
    } catch (error) {
      toast.error('Failed to create booking');
    }
  };

  return (
    <div>
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-zinc-100">Bookings</h1>
          <p className="text-zinc-400 mt-1">Manage all customer bookings</p>
        </div>
        <Button
          onClick={() => setShowCreateDialog(true)}
          className="bg-zinc-100 text-zinc-950 hover:bg-zinc-200"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Manual Booking
        </Button>
      </div>

      {/* Filters */}
      <Card className="bg-zinc-900 border-zinc-800 mb-6">
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <Input
                placeholder="Search by name, email, or booking ID..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 bg-zinc-950 border-zinc-800 text-zinc-100"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full md:w-48 bg-zinc-950 border-zinc-800 text-zinc-100">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="confirmed">Confirmed</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Bookings List */}
      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader>
          <CardTitle className="text-zinc-100">
            All Bookings ({filteredBookings.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-zinc-500 text-center py-8">Loading...</p>
          ) : filteredBookings.length === 0 ? (
            <p className="text-zinc-500 text-center py-8">No bookings found</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-zinc-800">
                    <th className="text-left py-3 px-4 text-zinc-400 font-medium">Booking ID</th>
                    <th className="text-left py-3 px-4 text-zinc-400 font-medium">Customer</th>
                    <th className="text-left py-3 px-4 text-zinc-400 font-medium">Service</th>
                    <th className="text-left py-3 px-4 text-zinc-400 font-medium">Date & Time</th>
                    <th className="text-left py-3 px-4 text-zinc-400 font-medium">Amount</th>
                    <th className="text-left py-3 px-4 text-zinc-400 font-medium">Status</th>
                    <th className="text-left py-3 px-4 text-zinc-400 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredBookings.map((booking) => (
                    <tr key={booking.bookingId} className="border-b border-zinc-800 hover:bg-zinc-800/50">
                      <td className="py-3 px-4 text-zinc-300 font-mono text-sm">{booking.bookingId}</td>
                      <td className="py-3 px-4">
                        <div className="text-zinc-100">{booking.customerName}</div>
                        <div className="text-xs text-zinc-500">{booking.email}</div>
                      </td>
                      <td className="py-3 px-4 text-zinc-300">{booking.serviceName}</td>
                      <td className="py-3 px-4">
                        <div className="text-zinc-300">{new Date(booking.date).toLocaleDateString()}</div>
                        <div className="text-xs text-zinc-500">{booking.time}</div>
                      </td>
                      <td className="py-3 px-4 text-zinc-100 font-semibold">€{booking.totalAmount}</td>
                      <td className="py-3 px-4">
                        <Select
                          defaultValue={booking.status}
                          onValueChange={(value) => handleStatusUpdate(booking.bookingId, value)}
                        >
                          <SelectTrigger className={`w-32 h-8 text-xs border-zinc-800 ${
                            booking.status === 'confirmed' ? 'bg-green-500/20 text-green-400' :
                            booking.status === 'completed' ? 'bg-blue-500/20 text-blue-400' :
                            booking.status === 'cancelled' ? 'bg-red-500/20 text-red-400' :
                            'bg-zinc-950 text-zinc-300'
                          }`}>
                            <SelectValue>
                              {booking.status?.charAt(0).toUpperCase() + booking.status?.slice(1)}
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pending">Pending</SelectItem>
                            <SelectItem value="confirmed">Confirmed</SelectItem>
                            <SelectItem value="completed">Completed</SelectItem>
                            <SelectItem value="cancelled">Cancelled</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setSelectedBooking(booking);
                              setShowDetails(true);
                            }}
                            className="text-zinc-400 hover:text-zinc-100"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDelete(booking.bookingId)}
                            className="text-red-400 hover:text-red-300"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Booking Details Dialog */}
      {selectedBooking && (
        <Dialog open={showDetails} onOpenChange={setShowDetails}>
          <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-w-2xl">
            <DialogHeader>
              <DialogTitle>Booking Details</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-sm text-zinc-500">Booking ID</div>
                  <div className="font-mono">{selectedBooking.bookingId}</div>
                </div>
                <div>
                  <div className="text-sm text-zinc-500">Status</div>
                  <div className="capitalize">{selectedBooking.status}</div>
                </div>
                <div>
                  <div className="text-sm text-zinc-500">Customer Name</div>
                  <div>{selectedBooking.customerName}</div>
                </div>
                <div>
                  <div className="text-sm text-zinc-500">Email</div>
                  <div>{selectedBooking.email}</div>
                </div>
                <div>
                  <div className="text-sm text-zinc-500">Phone</div>
                  <div>{selectedBooking.phone}</div>
                </div>
                <div>
                  <div className="text-sm text-zinc-500">Car Model</div>
                  <div>{selectedBooking.carModel || 'N/A'}</div>
                </div>
                <div className="col-span-2">
                  <div className="text-sm text-zinc-500">Address</div>
                  <div>{selectedBooking.address}</div>
                </div>
                <div>
                  <div className="text-sm text-zinc-500">Service</div>
                  <div>{selectedBooking.serviceName}</div>
                </div>
                <div>
                  <div className="text-sm text-zinc-500">Date & Time</div>
                  <div>{new Date(selectedBooking.date).toLocaleDateString()} at {selectedBooking.time}</div>
                </div>
                <div>
                  <div className="text-sm text-zinc-500">Service Price</div>
                  <div>€{selectedBooking.servicePrice}</div>
                </div>
                <div>
                  <div className="text-sm text-zinc-500">Total Amount</div>
                  <div className="font-semibold">€{selectedBooking.totalAmount}</div>
                </div>
                {selectedBooking.addOns && selectedBooking.addOns.length > 0 && (
                  <div className="col-span-2">
                    <div className="text-sm text-zinc-500 mb-2">Add-ons</div>
                    <div className="space-y-1">
                      {selectedBooking.addOns.map((addon, index) => (
                        <div key={index} className="text-sm">
                          {addon.name} - €{addon.price}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {selectedBooking.notes && (
                  <div className="col-span-2">
                    <div className="text-sm text-zinc-500">Notes</div>
                    <div className="text-sm">{selectedBooking.notes}</div>
                  </div>
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Create Manual Booking Dialog - Simplified for character limit */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Manual Booking</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-zinc-400">Customer Name *</label>
                <Input
                  value={manualBooking.customerName}
                  onChange={(e) => setManualBooking({...manualBooking, customerName: e.target.value})}
                  className="bg-zinc-950 border-zinc-800"
                />
              </div>
              <div>
                <label className="text-sm text-zinc-400">Email *</label>
                <Input
                  type="email"
                  value={manualBooking.email}
                  onChange={(e) => setManualBooking({...manualBooking, email: e.target.value})}
                  className="bg-zinc-950 border-zinc-800"
                />
              </div>
              <div>
                <label className="text-sm text-zinc-400">Phone *</label>
                <Input
                  value={manualBooking.phone}
                  onChange={(e) => setManualBooking({...manualBooking, phone: e.target.value})}
                  className="bg-zinc-950 border-zinc-800"
                />
              </div>
              <div>
                <label className="text-sm text-zinc-400">Service *</label>
                <Select value={manualBooking.serviceId} onValueChange={(value) => setManualBooking({...manualBooking, serviceId: value})}>
                  <SelectTrigger className="bg-zinc-950 border-zinc-800">
                    <SelectValue placeholder="Select service" />
                  </SelectTrigger>
                  <SelectContent>
                    {services.map(s => (
                      <SelectItem key={s.id} value={s.id}>{s.name} - €{s.price}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm text-zinc-400">Date *</label>
                <Input
                  type="date"
                  value={manualBooking.date}
                  onChange={(e) => setManualBooking({...manualBooking, date: e.target.value})}
                  className="bg-zinc-950 border-zinc-800"
                />
              </div>
              <div>
                <label className="text-sm text-zinc-400">Time *</label>
                <Input
                  value={manualBooking.time}
                  onChange={(e) => setManualBooking({...manualBooking, time: e.target.value})}
                  placeholder="e.g. 10:00 AM"
                  className="bg-zinc-950 border-zinc-800"
                />
              </div>
              <div className="col-span-2">
                <label className="text-sm text-zinc-400">Address *</label>
                <Input
                  value={manualBooking.address}
                  onChange={(e) => setManualBooking({...manualBooking, address: e.target.value})}
                  className="bg-zinc-950 border-zinc-800"
                />
              </div>
              <div>
                <label className="text-sm text-zinc-400">Payment Method</label>
                <Select value={manualBooking.paymentMethod} onValueChange={(value) => setManualBooking({...manualBooking, paymentMethod: value})}>
                  <SelectTrigger className="bg-zinc-950 border-zinc-800">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="card">Card</SelectItem>
                    <SelectItem value="transfer">Bank Transfer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Button onClick={handleCreateManual} className="w-full bg-zinc-100 text-zinc-950 hover:bg-zinc-200">
              Create Booking
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminBookings;
